// frontend/src/services/typedApiClient.ts
/**
 * Type-Safe API Client
 *
 * A fully type-safe API client generated from OpenAPI specification.
 * Provides compile-time type checking for:
 * - Request parameters (path, query, body)
 * - Response types
 * - HTTP methods per endpoint
 *
 * Usage:
 *   import { typedApi } from './typedApiClient';
 *
 *   // Fully typed - IDE autocomplete and error checking
 *   const courses = await typedApi.courses.search({ search: 'CMSC' });
 *   const plan = await typedApi.academicPlans.get(planId);
 */

import { apiClient } from '../utils/apiClient';
import type { paths, components } from '../types/api.generated';
import { PlanActionType } from '../adapters/plan';
import type { OptimizerMode } from '../types/optimizerModes';

// =============================================================================
// TYPE UTILITIES
// =============================================================================

/** Extract the response type for a given path and method */
type ResponseType<
  Path extends keyof paths,
  Method extends keyof paths[Path]
> = paths[Path][Method] extends { responses: { 200: { content: { 'application/json': infer R } } } }
  ? R
  : paths[Path][Method] extends { responses: { 201: { content: { 'application/json': infer R } } } }
  ? R
  : never;

/** Extract the request body type for a given path and method */
type RequestBody<
  Path extends keyof paths,
  Method extends keyof paths[Path]
> = paths[Path][Method] extends { requestBody: { content: { 'application/json': infer B } } }
  ? B
  : never;

/** Extract query parameters for a given path and method */
type QueryParams<
  Path extends keyof paths,
  Method extends keyof paths[Path]
> = paths[Path][Method] extends { parameters: { query?: infer Q } }
  ? Q
  : never;

/** Extract path parameters for a given path and method (exported for external use) */
export type PathParams<
  Path extends keyof paths,
  Method extends keyof paths[Path]
> = paths[Path][Method] extends { parameters: { path: infer P } }
  ? P
  : never;

/** Unwrap the data from standard API response format.
 * Mirrors `unwrapResponse` in apiClient.ts which returns `{success, ...data}`.
 * Tolerates the nullable `success` and `data` from OpenAPI 3.1 (Pydantic Optional). */
type UnwrapData<T> = T extends { success?: boolean | null; data?: infer D }
  ? { success: boolean } & NonNullable<D>
  : T;

// Re-export schema types for convenience
export type Schemas = components['schemas'];

// Common schema type aliases (matching actual generated schema names)
export type Course = Schemas['Course'];
export type AcademicPlan = Schemas['PlanDetail'];
export type APExam = Schemas['APExam'];
export type APMapping = Schemas['APMapping'];
export type Invite = Schemas['InviteCode'];
export type Feedback = Schemas['FeedbackItem'];
export type GenEdArea = Schemas['GenEdArea'];
export type TransferCourse = Schemas['TransferCourse'];
export type OtherCreditsCourse = Schemas['OtherCreditsCourse'];

// =============================================================================
// PREFERENCE TYPES
// =============================================================================
// These types enforce separation between app settings and AI-learned preferences.
// Using the wrong type will cause a compile-time error.

/**
 * User Preferences (stored in users.preferences)
 * App-level settings that the user explicitly controls.
 */
export interface UserPreferences {
  /** Whether the AI chatbot is enabled */
  chatbot_enabled?: boolean;
  /** UI theme preference */
  theme?: 'light' | 'dark' | 'system' | 'retro' | 'studio';
  /** Whether to receive email notifications */
  email_notifications?: boolean;
  /** Whether to show bookmarked courses in the UI */
  show_bookmarked_courses?: boolean;
}

/**
 * Student Profile Preferences (stored in student_profile.preferences)
 * Academic preferences learned from AI chat conversations.
 * Do NOT store app settings here.
 */
export interface StudentProfilePreferences {
  /** Maximum credits per semester */
  max_credits?: number;
  /** Whether to avoid Friday classes */
  avoid_friday_classes?: boolean;
  /** Preferred class times */
  preferred_class_times?: 'morning' | 'afternoon' | 'evening';
  /** Whether student prefers morning classes */
  prefers_morning?: boolean;
  /** Starting semester for planning */
  start_semester?: string;
}

// =============================================================================
// API MODULES
// =============================================================================

/**
 * AP Exams API
 */
const apExams = {
  /** List all AP exams */
  async list(): Promise<UnwrapData<ResponseType<'/api/ap/exams', 'get'>>> {
    return apiClient.get('/api/ap/exams');
  },

  /** Get score mappings for an AP exam */
  async getMappings(examId: number): Promise<UnwrapData<ResponseType<'/api/ap/exams/{exam_id}/mappings', 'get'>>> {
    return apiClient.get(`/api/ap/exams/${examId}/mappings`);
  },

  /** List user's AP scores */
  async listScores(): Promise<{ scores: unknown[]; total_count: number }> {
    return apiClient.get('/api/ap/scores');
  },

  /** Add an AP score */
  async addScore(data: {
    exam_name?: string;
    ap_exam_id?: number;
    score: number;
    year_taken?: number;
    course_choice?: string;
  }): Promise<{
    score_id: number;
    exam_name: string;
    score: number;
    evaluation: { total_credits: number; courses_awarded: string[] };
  }> {
    return apiClient.post('/api/ap/scores', data);
  },

  /** Check if an AP exam score requires a course/gen-ed choice */
  async checkChoice(data: {
    exam_name: string;
    score: number;
  }): Promise<{
    has_choice: boolean;
    choice_info: null | {
      type: 'course' | 'gen_ed';
      choice_group: string;
      options: Array<{
        value: string;
        label: string;
        is_recommended: boolean;
        recommendation_reason: string | null;
        gen_ed_codes: string[] | null;
      }>;
    };
  }> {
    return apiClient.post('/api/ap/scores/check-choice', data);
  },

  /** Delete an AP score */
  async deleteScore(scoreId: number): Promise<{
    evaluation: { total_credits: number; courses_awarded: string[] };
  }> {
    return apiClient.delete(`/api/ap/scores/${scoreId}`);
  },

  /** Evaluate AP credits */
  async evaluate(): Promise<{
    evaluation: {
      total_credits: number;
      plc_credits_used: number;
      plc_credits_capped: number;
      courses_awarded: number;
      elective_credits: number;
      advisor_notes: string[];
      courses: Array<{
        course_code: string;
        credits: number;
        source_exam: string;
        source_score: number;
      }>;
    };
  }> {
    return apiClient.post('/api/ap/evaluate');
  },

  /** Get AP status */
  async getStatus(): Promise<{
    status: {
      ap_scores_count: number;
      courses_count: number;
      total_credits: number;
      plc_credits_capped: number;
      evaluated: boolean;
      last_evaluated_at: string | null;
    };
  }> {
    return apiClient.get('/api/ap/status');
  },
};

/**
 * Courses API
 */
const courses = {
  /** Search courses with filters */
  async search(params?: QueryParams<'/api/courses/', 'get'>): Promise<UnwrapData<ResponseType<'/api/courses/', 'get'>>> {
    return apiClient.get('/api/courses', { params: params as Record<string, unknown> });
  },

  /** Get Gen Ed areas */
  async getGenEdAreas(): Promise<UnwrapData<ResponseType<'/api/courses/gen-ed-areas', 'get'>>> {
    return apiClient.get('/api/courses/gen-ed-areas');
  },

  /** Get Gen Ed summary */
  async getGenEdSummary(): Promise<UnwrapData<ResponseType<'/api/courses/gen-ed-summary', 'get'>>> {
    return apiClient.get('/api/courses/gen-ed-summary');
  },

  /** Get course areas */
  async getAreas(): Promise<UnwrapData<ResponseType<'/api/courses/areas', 'get'>>> {
    return apiClient.get('/api/courses/areas');
  },

  /** Get course availability */
  async getAvailability(courseCode: string): Promise<UnwrapData<ResponseType<'/api/courses/{course_code}/availability', 'get'>>> {
    return apiClient.get(`/api/courses/${courseCode}/availability`);
  },

  /** Check course in semester */
  async checkSemester(courseCode: string, semesterCode: string): Promise<UnwrapData<ResponseType<'/api/courses/{course_code}/semester/{semester_code}', 'get'>>> {
    return apiClient.get(`/api/courses/${courseCode}/semester/${semesterCode}`);
  },

  /** Search courses by filter */
  async searchByFilter(data: RequestBody<'/api/courses/by-filter', 'post'>): Promise<UnwrapData<ResponseType<'/api/courses/by-filter', 'post'>>> {
    return apiClient.post('/api/courses/by-filter', data);
  },

  /** Get a single course by code */
  async get(courseCode: string): Promise<Course | null> {
    return apiClient.get(`/api/courses/${encodeURIComponent(courseCode)}`);
  },
};

/**
 * Academic Plans API
 */
/** One course in a multi-course add (PUT action 'add_courses', sent via
 *  useCourseOperations.addCourses). */
export interface AddCoursesItem {
  course_code: string;
  grade?: string | null;
  /** OR-group areas the student picked instead of the default. */
  gen_ed_choices?: string[];
}

export interface AddCoursesResultEntry {
  course_code: string;
  status: 'added' | 'failed';
  message?: string;
  title?: string;
  credits?: number;
  grade?: string | null;
  warnings: string[];
}

export interface AddCoursesResult {
  success: boolean;
  semester: string;
  added: number;
  failed: number;
  results: AddCoursesResultEntry[];
}

export interface GenEdOrGroup {
  options: string[];
  default: string;
}

export interface GenEdPreviewCourse {
  course_code: string;
  or_groups: GenEdOrGroup[];
}

const academicPlans = {
  /** List user's academic plans */
  async list(): Promise<UnwrapData<ResponseType<'/api/academic-plans', 'get'>>> {
    return apiClient.get('/api/academic-plans');
  },

  /** Create a new academic plan */
  async create(data: RequestBody<'/api/academic-plans', 'post'>): Promise<UnwrapData<ResponseType<'/api/academic-plans', 'post'>>> {
    return apiClient.post('/api/academic-plans', data);
  },

  /** Get academic plan details (includes optimizer suggestions) */
  async get(planId: number): Promise<UnwrapData<ResponseType<'/api/academic-plans/{plan_id}', 'get'>>> {
    return apiClient.get(`/api/academic-plans/${planId}?include_suggestions=true`);
  },

  /** Clear all courses from a plan */
  async clear(
    planId: number,
    options?: { includePlaceholders?: boolean }
  ): Promise<UnwrapData<ResponseType<'/api/academic-plans/{plan_id}/clear', 'post'>>> {
    // Placeholders (#205) survive a clear unless explicitly included.
    return apiClient.post(`/api/academic-plans/${planId}/clear`, {
      include_placeholders: options?.includePlaceholders ?? false,
    });
  },

  /** Copy plan content */
  async copy(planId: number, data: RequestBody<'/api/academic-plans/{plan_id}/copy', 'post'>): Promise<UnwrapData<ResponseType<'/api/academic-plans/{plan_id}/copy', 'post'>>> {
    return apiClient.post(`/api/academic-plans/${planId}/copy`, data);
  },

  /** Get degree audit for plan */
  async getAudit(planId: number): Promise<UnwrapData<ResponseType<'/api/academic-plans/{plan_id}/adrl-audit', 'get'>>> {
    return apiClient.get(`/api/academic-plans/${planId}/adrl-audit`);
  },

  /** Set a plan as active */
  async setActive(planId: number): Promise<void> {
    return apiClient.put(`/api/academic-plans/${planId}`, {
      action: PlanActionType.SetActive
    });
  },

  /** Rename a plan */
  async rename(planId: number, planName: string): Promise<void> {
    return apiClient.put(`/api/academic-plans/${planId}`, {
      action: PlanActionType.UpdateName,
      plan_name: planName
    });
  },

  /** Update plan programs (major/minor/citation) - supports both single codes and arrays */
  async updatePrograms(planId: number, updates: {
    major_code?: string | null;
    minor_code?: string | null;
    major_catalog_year?: string;
    minor_catalog_year?: string;
    majors?: Array<{ program: string; track: string }>;
    minors?: Array<{ program: string; track: string }>;
    citations?: Array<{ program: string; track: string }>;
    // Prior-stack Phase 5: pre-health advising overlays (warning-tier). Preserved
    // server-side when omitted; only sent when the overlay list actually changes.
    advising_overlays?: Array<{ program: string; track: string }>;
  }): Promise<void> {
    return apiClient.put(`/api/academic-plans/${planId}`, {
      action: PlanActionType.UpdatePrograms,
      ...updates
    });
  },

  /** Reorder programs (majors, minors, or citations) */
  async reorderPrograms(planId: number, updates: {
    majors?: Array<{ program: string; track: string }>;
    minors?: Array<{ program: string; track: string }>;
    citations?: Array<{ program: string; track: string }>;
  }): Promise<void> {
    return apiClient.put(`/api/academic-plans/${planId}`, {
      action: PlanActionType.ReorderPrograms,
      ...updates
    });
  },

  /** Update graduation credit target (120 or 150) */
  async updateCreditTarget(planId: number, creditTarget: number): Promise<unknown> {
    return apiClient.put(`/api/academic-plans/${planId}`, {
      action: PlanActionType.UpdateCreditTarget,
      credit_target: creditTarget,
    });
  },

  /** The OR gen-ed area each course would default to if added in this order.
   *  Read-only; the defaults match what addCourses stores. */
  async previewGenEd(planId: number, courseCodes: string[]): Promise<{ courses: GenEdPreviewCourse[] }> {
    return apiClient.post(`/api/academic-plans/${planId}/gen-ed-preview`, {
      course_codes: courseCodes,
    });
  },

  /** Update the plan's progress marker — the term the student is currently in
   * (completion derives from it; see chatbot-credit-grounding plan). */
  async updateCurrentTerm(planId: number, currentTerm: number): Promise<unknown> {
    return apiClient.put(`/api/academic-plans/${planId}`, {
      action: PlanActionType.UpdateCurrentTerm,
      current_term: currentTerm,
    });
  },

  /** Generic update with action parameter */
  async update(planId: number, action: string, data: Record<string, unknown>): Promise<unknown> {
    return apiClient.put(`/api/academic-plans/${planId}`, {
      action,
      ...data
    });
  },

  /** Remove a course from a plan */
  async removeCourse(planId: number, semester: string, courseCode: string): Promise<void> {
    return apiClient.put(`/api/academic-plans/${planId}`, {
      action: PlanActionType.RemoveCourse,
      semester,
      course_code: courseCode
    });
  },

  /**
   * Update plan semesters.
   *
   * If the new selection would delete any semester that still holds courses, the
   * backend rejects with HTTP 409 (error_code 'destructive_semester_change')
   * unless confirmDestructive is true — see RC5-1. Callers should catch the 409,
   * surface the would-delete list (err.details.details.semesters_to_delete), and
   * retry with confirmDestructive=true only after the user confirms.
   */
  async updateSemesters(
    planId: number,
    selectedSemesters: string[],
    confirmDestructive: boolean = false,
  ): Promise<void> {
    return apiClient.put(`/api/academic-plans/${planId}/semesters`, {
      selected_semesters: selectedSemesters,
      confirm_destructive: confirmDestructive,
    });
  },

  /** Add a course to bookmarks */
  async addBookmark(planId: number, courseCode: string): Promise<unknown> {
    return apiClient.post(`/api/academic-plans/${planId}/bookmarks`, {
      course_code: courseCode
    });
  },

  /** Remove a course from bookmarks */
  async removeBookmark(planId: number, courseCode: string): Promise<unknown> {
    return apiClient.delete(`/api/academic-plans/${planId}/bookmarks/${courseCode}`);
  },

  /** Get available semesters for a plan */
  async getAvailableSemesters(planId: number): Promise<{
    semesters_by_year?: Record<string, unknown[]>;
    semesters?: unknown[];
  }> {
    return apiClient.get(`/api/academic-plans/${planId}/available-semesters`);
  },

  /** Reinitialize semesters based on start date */
  async reinitializeSemesters(planId: number, startSemester: string): Promise<unknown> {
    return apiClient.post(`/api/academic-plans/${planId}/reinitialize-semesters`, {
      start_semester: startSemester
    });
  },

  /** Get available start semesters */
  async getAvailableStartSemesters(): Promise<{ semesters: Array<{ value: string; label: string }> }> {
    return apiClient.get('/api/available-start-semesters');
  },
};

/**
 * Programs API
 */
const programs = {
  /** Get programs in hierarchical structure */
  async getHierarchical(): Promise<UnwrapData<ResponseType<'/api/programs-hierarchical', 'get'>>> {
    return apiClient.get('/api/programs-hierarchical');
  },

  /** Get all available programs */
  async getAvailable(): Promise<UnwrapData<ResponseType<'/api/available-programs', 'get'>>> {
    return apiClient.get('/api/available-programs');
  },
};

/**
 * User Preferences API
 * For app-level settings (theme, chatbot_enabled, email_notifications).
 * Do NOT use for academic preferences - use studentProfile.preferences instead.
 */
const userPreferences = {
  /** Get user preferences (app settings) */
  async get(): Promise<{ success?: boolean; preferences: UserPreferences }> {
    return apiClient.get('/api/user/preferences');
  },

  /** Update user preferences (app settings) */
  async update(data: Partial<UserPreferences>): Promise<{ success?: boolean; preferences: UserPreferences }> {
    return apiClient.patch('/api/user/preferences', data);
  },
};

/**
 * Users API
 */
const users = {
  /** Sync user with Supabase */
  async sync(data: RequestBody<'/api/users/sync', 'post'>): Promise<UnwrapData<ResponseType<'/api/users/sync', 'post'>>> {
    // `retryUnsafeMethods`: apiClient does not re-send a POST after a transport
    // failure, because it cannot know whether the server committed. This endpoint is
    // safe to re-send, but NOT because of `ON CONFLICT` — `INSERT INTO users`
    // (services/user_service/sync.py) has no such clause. It is idempotent because
    // it looks the row up first and inserts within one transaction. A concurrent
    // duplicate for a new identity waits on a per-identity advisory lock (8.71.1),
    // then finds the row and takes the update branch; the unique constraints
    // `users_supabase_user_id_key` and `users_email_key` stay as the backstop, and a
    // violation of either is a 500, itself retryable. The membership insert and the
    // invitation consume are separately guarded (`ON CONFLICT (user_id,
    // institution_id) DO NOTHING`, and `used_at IS NULL`).
    //
    // Worth opting in because this sits on the login critical path: a single blip
    // leaves `dbUser` null, and AppContext's sync effect does not re-run, so the
    // user lands in the app with no plan.
    return apiClient.post('/api/users/sync', data, { retry: { retryUnsafeMethods: true } });
  },

  /** Get user profile */
  async getProfile(userId: number): Promise<UnwrapData<ResponseType<'/api/users/{user_id}/profile', 'get'>>> {
    return apiClient.get(`/api/users/${userId}/profile`);
  },

  /** Update user profile */
  async updateProfile(userId: number, data: RequestBody<'/api/users/{user_id}/profile', 'put'>): Promise<UnwrapData<ResponseType<'/api/users/{user_id}/profile', 'put'>>> {
    return apiClient.put(`/api/users/${userId}/profile`, data);
  },

  /** Update enrollment dates */
  async updateEnrollment(userId: number, data: RequestBody<'/api/users/{user_id}/enrollment', 'put'>): Promise<UnwrapData<ResponseType<'/api/users/{user_id}/enrollment', 'put'>>> {
    return apiClient.put(`/api/users/${userId}/enrollment`, data);
  },

  /** Check email availability */
  async checkEmail(data: RequestBody<'/api/users/check-email', 'post'>): Promise<UnwrapData<ResponseType<'/api/users/check-email', 'post'>>> {
    return apiClient.post('/api/users/check-email', data);
  },

  /** Reset user data */
  async reset(data: RequestBody<'/api/users/reset', 'post'>): Promise<UnwrapData<ResponseType<'/api/users/reset', 'post'>>> {
    return apiClient.post('/api/users/reset', data);
  },

  /** Accept the current Terms of Service and Privacy Policy (409 on a stale version) */
  async acceptTerms(data: RequestBody<'/api/users/me/accept-terms', 'post'>): Promise<UnwrapData<ResponseType<'/api/users/me/accept-terms', 'post'>>> {
    return apiClient.post('/api/users/me/accept-terms', data);
  },

  /**
   * FERPA §99.32 disclosure history for the current user.
   *
   * SCHEMA GAP: the route is in the spec but documents no 200 response body,
   * so `ResponseType<>` resolves to `never`. The shape is stated here until
   * the endpoint gets a `.response(200, ...)` — see the S5 report.
   */
  async getDisclosures(): Promise<{ disclosures?: unknown[]; count?: number }> {
    return apiClient.get('/api/users/me/disclosures');
  },
};

/**
 * Feedback API
 */
const feedback = {
  /** Submit feedback */
  async submit(data: RequestBody<'/api/feedback', 'post'>): Promise<UnwrapData<ResponseType<'/api/feedback', 'post'>>> {
    return apiClient.post('/api/feedback', data);
  },

  /** Get feedback history */
  async getHistory(params?: QueryParams<'/api/feedback/history', 'get'>): Promise<UnwrapData<ResponseType<'/api/feedback/history', 'get'>>> {
    return apiClient.get('/api/feedback/history', { params: params as Record<string, unknown> });
  },

  /** Health check */
  async health(): Promise<UnwrapData<ResponseType<'/api/feedback/health', 'get'>>> {
    return apiClient.get('/api/feedback/health');
  },
};

/**
 * Invites API
 */
const invites = {
  /** Validate invite code (public, no auth required) */
  async validate(data: RequestBody<'/api/invites/validate', 'post'>): Promise<UnwrapData<ResponseType<'/api/invites/validate', 'post'>>> {
    return apiClient.post('/api/invites/validate', data, { skipAuth: true });
  },

  /** Get user invite codes */
  async list(): Promise<UnwrapData<ResponseType<'/api/invites', 'get'>>> {
    return apiClient.get('/api/invites');
  },

  /** Get invite balance */
  async getBalance(): Promise<UnwrapData<ResponseType<'/api/invites/balance', 'get'>>> {
    return apiClient.get('/api/invites/balance');
  },

  /** Get inviter info */
  async getInviter(): Promise<UnwrapData<ResponseType<'/api/invites/inviter', 'get'>>> {
    return apiClient.get('/api/invites/inviter');
  },

  /** Get invitees list */
  async getInvitees(): Promise<UnwrapData<ResponseType<'/api/invites/invitees', 'get'>>> {
    return apiClient.get('/api/invites/invitees');
  },
};

/**
 * Chat API
 */
const chat = {
  /** Get available models */
  async getModels(): Promise<UnwrapData<ResponseType<'/api/chat/models', 'get'>>> {
    return apiClient.get('/api/chat/models');
  },

  /** Get chat status */
  async getStatus(): Promise<UnwrapData<ResponseType<'/api/chat/status', 'get'>>> {
    return apiClient.get('/api/chat/status');
  },

  /** Get usage stats */
  async getUsage(): Promise<UnwrapData<ResponseType<'/api/chat/usage', 'get'>>> {
    return apiClient.get('/api/chat/usage');
  },

  /** Get starter questions */
  async getStarterQuestions(data?: RequestBody<'/api/chat/starter-questions', 'post'>): Promise<UnwrapData<ResponseType<'/api/chat/starter-questions', 'post'>>> {
    return apiClient.post('/api/chat/starter-questions', data);
  },

  // ==========================================================================
  // Agent V4 API (V4 is the only chat system)
  // ==========================================================================

  /**
   * Confirm or reject V4 pending actions.
   *
   * Non-default timeout/retry, deliberately: confirming can execute
   * `generate_suggestions`, which runs the optimizer INLINE (C6 aligned the
   * confirm path with the immediate path). Unlike the immediate path this is a
   * plain POST with no streamed progress, so the whole run is dead air.
   *  - `timeout`: the 30s default is well under the backend's 120s gunicorn
   *    timeout, so a slow-but-succeeding run would abort client-side while the
   *    server keeps going. Match the server instead.
   *  - `retry: false`: a re-sent POST restores the same still-uncleared pending
   *    actions from Redis and executes them again — and `run_recompute_sync` does
   *    not take the per-plan single-flight lock that the queue consumer does.
   *    Confirmation is not idempotent; never auto-retry it — with one documented
   *    exception: `retry: false` does NOT suppress the 401 force-refresh re-send
   *    (`attempt--; continue` is gated by neither flag), so this can still go out
   *    twice after a token refresh. Harmless in practice, since a 401 proves the
   *    handler never ran, but tracked as #376 rather than left implied. apiClient no longer
   *    re-sends a non-idempotent method after a transport failure (that was a
   *    general bug, fixed), so this is now belt-and-braces — but it is kept
   *    deliberately, because it ALSO suppresses the status-based retries
   *    (`retryableStatuses` includes 500/502/503/504) which a received response
   *    would still trigger.
   */
  async confirmV4Actions(data: {
    academic_plan_id: number;
    confirm: boolean;
  }): Promise<{
    executed: boolean;
    success?: boolean;
    error?: string;
    rejected?: boolean;
    actions_count: number;
  }> {
    return apiClient.post('/api/chat/v4/confirm', data, {
      timeout: 120000,
      retry: false,
    });
  },

  /** Get V4 pending actions */
  async getV4PendingActions(academicPlanId: number): Promise<{
    pending_actions: Array<{
      action_type: string;
      parameters: Record<string, unknown>;
    }>;
  }> {
    return apiClient.get(`/api/chat/v4/pending?academic_plan_id=${academicPlanId}`);
  },

  /** Reset V4 session */
  async resetV4Session(academicPlanId: number): Promise<{
    reset: boolean;
    message: string;
  }> {
    return apiClient.post('/api/chat/v4/reset', { academic_plan_id: academicPlanId });
  },
};

/**
 * Grades API
 */
const grades = {
  /** Update a grade */
  async update(data: RequestBody<'/api/grades/update', 'put'>): Promise<UnwrapData<ResponseType<'/api/grades/update', 'put'>>> {
    return apiClient.put('/api/grades/update', data);
  },

  /** Get a grade */
  async get(params: QueryParams<'/api/grades/get', 'get'>): Promise<UnwrapData<ResponseType<'/api/grades/get', 'get'>>> {
    return apiClient.get('/api/grades/get', { params: params as Record<string, unknown> });
  },
};

/**
 * Other Credits API
 */
const otherCredits = {
  /** Ensure other credits semester exists */
  async ensureSemester(planId: number): Promise<UnwrapData<ResponseType<'/api/other-credits/ensure/{plan_id}', 'post'>>> {
    return apiClient.post(`/api/other-credits/ensure/${planId}`);
  },

  /** Get other credits courses for plan */
  async getCourses(planId: number): Promise<UnwrapData<ResponseType<'/api/other-credits/plan/{plan_id}/courses', 'get'>>> {
    return apiClient.get(`/api/other-credits/plan/${planId}/courses`);
  },

  /** Get PLC summary */
  async getPlcSummary(planId: number): Promise<UnwrapData<ResponseType<'/api/other-credits/plc-summary/{plan_id}', 'get'>>> {
    return apiClient.get(`/api/other-credits/plc-summary/${planId}`);
  },
};

/**
 * Transfer Credits API
 */
const transferCredits = {
  /** Ensure transfer semester exists */
  async ensureSemester(planId: number): Promise<UnwrapData<ResponseType<'/api/transfer-credits/ensure/{plan_id}', 'post'>>> {
    return apiClient.post(`/api/transfer-credits/ensure/${planId}`);
  },

  /** Get transfer summary for user */
  async getSummary(userId: number): Promise<UnwrapData<ResponseType<'/api/transfer-credits/summary/{user_id}', 'get'>>> {
    return apiClient.get(`/api/transfer-credits/summary/${userId}`);
  },

  /** Validate transfer credits */
  async validate(data: RequestBody<'/api/transfer-credits/validate-credits', 'post'>): Promise<UnwrapData<ResponseType<'/api/transfer-credits/validate-credits', 'post'>>> {
    return apiClient.post('/api/transfer-credits/validate-credits', data);
  },

  /** Add transfer note */
  async addNote(data: RequestBody<'/api/transfer-credits/add-note', 'post'>): Promise<UnwrapData<ResponseType<'/api/transfer-credits/add-note', 'post'>>> {
    return apiClient.post('/api/transfer-credits/add-note', data);
  },

  /** Get transfer courses for plan */
  async getCourses(planId: number): Promise<UnwrapData<ResponseType<'/api/transfer-credits/plan/{plan_id}/courses', 'get'>>> {
    return apiClient.get(`/api/transfer-credits/plan/${planId}/courses`);
  },

  /** Check if course has transfer credit */
  async checkCourse(planId: number, courseCode: string): Promise<UnwrapData<ResponseType<'/api/transfer-credits/plan/{plan_id}/check/{course_code}', 'get'>>> {
    return apiClient.get(`/api/transfer-credits/plan/${planId}/check/${courseCode}`);
  },
};

/**
 * Requirements API
 */
const requirements = {
  /** Get program requirements */
  async get(programCode: string): Promise<UnwrapData<ResponseType<'/api/requirements/{program_code}', 'get'>>> {
    return apiClient.get(`/api/requirements/${programCode}`);
  },

  /** Get hierarchical requirements */
  async getHierarchical(programCode: string, params?: QueryParams<'/api/requirements/{program_code}/hierarchical', 'get'>): Promise<UnwrapData<ResponseType<'/api/requirements/{program_code}/hierarchical', 'get'>>> {
    return apiClient.get(`/api/requirements/${programCode}/hierarchical`, { params: params as Record<string, unknown> });
  },
};

/**
 * Student Profile API (v2)
 * For AI-learned academic preferences and insights.
 * Do NOT use for app settings (chatbot_enabled, theme) - use userPreferences instead.
 */
const studentProfile = {
  /** Get full profile */
  async get(): Promise<UnwrapData<ResponseType<'/api/v2/profile', 'get'>>> {
    return apiClient.get('/api/v2/profile');
  },

  /** Get profile settings */
  async getSettings(): Promise<UnwrapData<ResponseType<'/api/v2/profile/settings', 'get'>>> {
    return apiClient.get('/api/v2/profile/settings');
  },

  /** Update profile settings */
  async updateSettings(data: RequestBody<'/api/v2/profile/settings', 'put'>): Promise<UnwrapData<ResponseType<'/api/v2/profile/settings', 'put'>>> {
    return apiClient.put('/api/v2/profile/settings', data);
  },

  /** Get preferences (AI-learned academic preferences) */
  async getPreferences(): Promise<StudentProfilePreferences> {
    return apiClient.get('/api/v2/profile/preferences');
  },

  /** Update preferences (AI-learned academic preferences) */
  async updatePreferences(data: Partial<StudentProfilePreferences>): Promise<StudentProfilePreferences> {
    return apiClient.patch('/api/v2/profile/preferences', data);
  },

  /** Get concerns */
  async getConcerns(): Promise<UnwrapData<ResponseType<'/api/v2/profile/concerns', 'get'>>> {
    return apiClient.get('/api/v2/profile/concerns');
  },

  /** Add concerns */
  async addConcerns(data: RequestBody<'/api/v2/profile/concerns', 'post'>): Promise<UnwrapData<ResponseType<'/api/v2/profile/concerns', 'post'>>> {
    return apiClient.post('/api/v2/profile/concerns', data);
  },

  /** Remove concern */
  async removeConcern(params: QueryParams<'/api/v2/profile/concerns', 'delete'>): Promise<UnwrapData<ResponseType<'/api/v2/profile/concerns', 'delete'>>> {
    return apiClient.delete('/api/v2/profile/concerns', { params: params as Record<string, unknown> });
  },

  /** Get interests */
  async getInterests(): Promise<UnwrapData<ResponseType<'/api/v2/profile/interests', 'get'>>> {
    return apiClient.get('/api/v2/profile/interests');
  },

  /** Add interests */
  async addInterests(data: RequestBody<'/api/v2/profile/interests', 'post'>): Promise<UnwrapData<ResponseType<'/api/v2/profile/interests', 'post'>>> {
    return apiClient.post('/api/v2/profile/interests', data);
  },

  /** Log decision */
  async logDecision(data: RequestBody<'/api/v2/profile/decisions', 'post'>): Promise<UnwrapData<ResponseType<'/api/v2/profile/decisions', 'post'>>> {
    return apiClient.post('/api/v2/profile/decisions', data);
  },

  /** Regenerate narrative */
  async regenerateNarrative(): Promise<UnwrapData<ResponseType<'/api/v2/profile/narrative/regenerate', 'post'>>> {
    return apiClient.post('/api/v2/profile/narrative/regenerate');
  },

  /** Store pending session */
  async storeSession(data: RequestBody<'/api/v2/profile/session', 'post'>): Promise<UnwrapData<ResponseType<'/api/v2/profile/session', 'post'>>> {
    return apiClient.post('/api/v2/profile/session', data);
  },

  /** Summarize session */
  async summarizeSession(): Promise<UnwrapData<ResponseType<'/api/v2/profile/session/summarize', 'post'>>> {
    return apiClient.post('/api/v2/profile/session/summarize');
  },

  /** Remove one chat-derived interest */
  async removeInterest(params: QueryParams<'/api/v2/profile/interests', 'delete'>): Promise<UnwrapData<ResponseType<'/api/v2/profile/interests', 'delete'>>> {
    return apiClient.delete('/api/v2/profile/interests', { params: params as Record<string, unknown> });
  },

  /** Clear the AI-generated narrative */
  async clearNarrative(): Promise<UnwrapData<ResponseType<'/api/v2/profile/narrative', 'delete'>>> {
    return apiClient.delete('/api/v2/profile/narrative');
  },

  /** Get interest/avoidance tags + resolved previews + starter chips */
  async getInterestTags(): Promise<UnwrapData<ResponseType<'/api/v2/profile/interest-tags', 'get'>>> {
    return apiClient.get('/api/v2/profile/interest-tags');
  },

  /** Save interest/avoidance tags */
  async updateInterestTags(data: RequestBody<'/api/v2/profile/interest-tags', 'put'>): Promise<UnwrapData<ResponseType<'/api/v2/profile/interest-tags', 'put'>>> {
    return apiClient.put('/api/v2/profile/interest-tags', data);
  },

  /** Resolve one tag to a preview of matching courses */
  async resolveInterestTag(data: RequestBody<'/api/v2/profile/interest-tags/resolve', 'post'>): Promise<UnwrapData<ResponseType<'/api/v2/profile/interest-tags/resolve', 'post'>>> {
    return apiClient.post('/api/v2/profile/interest-tags/resolve', data);
  },

  /** Remove/restore one course from a resolved tag */
  async curateInterestTag(data: RequestBody<'/api/v2/profile/interest-tags/curation', 'post'>): Promise<UnwrapData<ResponseType<'/api/v2/profile/interest-tags/curation', 'post'>>> {
    return apiClient.post('/api/v2/profile/interest-tags/curation', data);
  },

  /** Get difficulty stance + term willingness */
  async getStance(): Promise<UnwrapData<ResponseType<'/api/v2/profile/stance', 'get'>>> {
    return apiClient.get('/api/v2/profile/stance');
  },

  /** Update difficulty stance + term willingness (partial) */
  async updateStance(data: RequestBody<'/api/v2/profile/stance', 'put'>): Promise<UnwrapData<ResponseType<'/api/v2/profile/stance', 'put'>>> {
    return apiClient.put('/api/v2/profile/stance', data);
  },
};

/**
 * Plan overrides API (W24 — student-recorded constraint deviations)
 */
const overrides = {
  /** List a plan's overrides */
  async list(planId: number, params?: QueryParams<'/api/plans/{plan_id}/overrides', 'get'>): Promise<UnwrapData<ResponseType<'/api/plans/{plan_id}/overrides', 'get'>>> {
    return apiClient.get(`/api/plans/${planId}/overrides`, { params: params as Record<string, unknown> });
  },

  /** Create an override */
  async create(planId: number, data: RequestBody<'/api/plans/{plan_id}/overrides', 'post'>): Promise<UnwrapData<ResponseType<'/api/plans/{plan_id}/overrides', 'post'>>> {
    return apiClient.post(`/api/plans/${planId}/overrides`, data);
  },

  /** Get one override */
  async get(planId: number, overrideId: number): Promise<UnwrapData<ResponseType<'/api/plans/{plan_id}/overrides/{override_id}', 'get'>>> {
    return apiClient.get(`/api/plans/${planId}/overrides/${overrideId}`);
  },

  /** Revoke an override */
  async revoke(planId: number, overrideId: number, data: RequestBody<'/api/plans/{plan_id}/overrides/{override_id}/revoke', 'post'>): Promise<UnwrapData<ResponseType<'/api/plans/{plan_id}/overrides/{override_id}/revoke', 'post'>>> {
    return apiClient.post(`/api/plans/${planId}/overrides/${overrideId}/revoke`, data);
  },
};

/**
 * Admin Users API
 */
const adminUsers = {
  /** List users */
  async list(params?: QueryParams<'/api/admin/users', 'get'>): Promise<UnwrapData<ResponseType<'/api/admin/users', 'get'>>> {
    return apiClient.get('/api/admin/users', { params: params as Record<string, unknown> });
  },

  /** Create a new user via Supabase invite */
  async create(data: RequestBody<'/api/admin/users', 'post'>): Promise<UnwrapData<ResponseType<'/api/admin/users', 'post'>>> {
    return apiClient.post('/api/admin/users', data);
  },

  /** Get user details */
  async get(userId: number): Promise<UnwrapData<ResponseType<'/api/admin/users/{user_id}', 'get'>>> {
    return apiClient.get(`/api/admin/users/${userId}`);
  },

  /** Delete user */
  async delete(userId: number): Promise<UnwrapData<ResponseType<'/api/admin/users/{user_id}', 'delete'>>> {
    return apiClient.delete(`/api/admin/users/${userId}`);
  },

  /** Update user role */
  async updateRole(userId: number, data: RequestBody<'/api/admin/users/{user_id}/role', 'put'>): Promise<UnwrapData<ResponseType<'/api/admin/users/{user_id}/role', 'put'>>> {
    return apiClient.put(`/api/admin/users/${userId}/role`, data);
  },

  /** Update user status */
  async updateStatus(userId: number, data: RequestBody<'/api/admin/users/{user_id}/status', 'put'>): Promise<UnwrapData<ResponseType<'/api/admin/users/{user_id}/status', 'put'>>> {
    return apiClient.put(`/api/admin/users/${userId}/status`, data);
  },

  /** Get user stats */
  async getStats(): Promise<UnwrapData<ResponseType<'/api/admin/users/stats', 'get'>>> {
    return apiClient.get('/api/admin/users/stats');
  },

  /** Get user activity */
  async getActivity(userId: number, params?: { limit?: number; offset?: number }): Promise<{
    activities: unknown[];
    total_count: number;
    has_more: boolean;
  }> {
    return apiClient.get(`/api/admin/users/${userId}/activity`, { params });
  },

  /** Get user plans */
  async getPlans(userId: number): Promise<UnwrapData<ResponseType<'/api/admin/users/{user_id}/plans', 'get'>>> {
    return apiClient.get(`/api/admin/users/${userId}/plans`);
  },

  /** Reset user data */
  async reset(userId: number, data: {
    reset_options: {
      chat_history?: boolean;
      academic_plans?: boolean;
      programs?: boolean;
      personal_info?: boolean;
    };
  }): Promise<{
    user_id: number;
    email: string;
    reset_summary: unknown;
  }> {
    return apiClient.post(`/api/admin/users/${userId}/reset`, data);
  },
};

/**
 * Admin Chats API
 */
const adminChats = {
  /** Get chat stats */
  async getStats(params?: QueryParams<'/api/admin/chats/stats', 'get'>): Promise<UnwrapData<ResponseType<'/api/admin/chats/stats', 'get'>>> {
    return apiClient.get('/api/admin/chats/stats', { params: params as Record<string, unknown> });
  },

  /** List conversations */
  async listConversations(params?: QueryParams<'/api/admin/chats/conversations', 'get'>): Promise<UnwrapData<ResponseType<'/api/admin/chats/conversations', 'get'>>> {
    return apiClient.get('/api/admin/chats/conversations', { params: params as Record<string, unknown> });
  },

  /** Get conversation */
  async getConversation(conversationId: number): Promise<UnwrapData<ResponseType<'/api/admin/chats/conversations/{conversation_id}', 'get'>>> {
    return apiClient.get(`/api/admin/chats/conversations/${conversationId}`);
  },

  /** Get user's chats (accepts numeric ID or Supabase UUID) */
  async getUserChats(userId: number | string, params?: QueryParams<'/api/admin/chats/users/{user_id}', 'get'>): Promise<UnwrapData<ResponseType<'/api/admin/chats/users/{user_id}', 'get'>>> {
    return apiClient.get(`/api/admin/chats/users/${userId}`, { params: params as Record<string, unknown> });
  },
};

/**
 * Admin Feedback API
 */
const adminFeedback = {
  /** List all feedback */
  async list(params?: QueryParams<'/api/admin/feedback', 'get'>): Promise<UnwrapData<ResponseType<'/api/admin/feedback', 'get'>>> {
    return apiClient.get('/api/admin/feedback', { params: params as Record<string, unknown> });
  },

  /** Update feedback */
  async update(feedbackId: number, data: RequestBody<'/api/admin/feedback/{feedback_id}', 'put'>): Promise<UnwrapData<ResponseType<'/api/admin/feedback/{feedback_id}', 'put'>>> {
    return apiClient.put(`/api/admin/feedback/${feedbackId}`, data);
  },

  /** Get feedback stats */
  async getStats(): Promise<UnwrapData<ResponseType<'/api/admin/feedback/stats', 'get'>>> {
    return apiClient.get('/api/admin/feedback/stats');
  },
};

/**
 * Admin Invites API
 */
const adminInvites = {
  /** List all invites */
  async list(params?: QueryParams<'/api/admin/invites', 'get'>): Promise<UnwrapData<ResponseType<'/api/admin/invites', 'get'>>> {
    return apiClient.get('/api/admin/invites', { params: params as Record<string, unknown> });
  },

  /** Get invite stats */
  async getStats(): Promise<UnwrapData<ResponseType<'/api/admin/invites/stats', 'get'>>> {
    return apiClient.get('/api/admin/invites/stats');
  },

  /** Generate invites for user */
  async generateForUser(data: RequestBody<'/api/admin/invites/generate-for-user', 'post'>): Promise<UnwrapData<ResponseType<'/api/admin/invites/generate-for-user', 'post'>>> {
    return apiClient.post('/api/admin/invites/generate-for-user', data);
  },

  /** Revoke an invite code */
  async revoke(code: string): Promise<{ message: string }> {
    return apiClient.post(`/api/admin/invites/${code}/revoke`);
  },
};

/**
 * Admin Email Invitations API (Phase G3 multi-membership architecture).
 * Scoped to the admin's active institution server-side.
 */
const adminEmailInvitations = {
  /** List email invitations scoped to the admin's active institution. */
  async list(params?: { include_used?: boolean }): Promise<UnwrapData<ResponseType<'/api/admin/email-invitations', 'get'>>> {
    return apiClient.get('/api/admin/email-invitations', { params: params as Record<string, unknown> });
  },

  /** Bulk-create email invitations for the admin's active institution. */
  async create(data: RequestBody<'/api/admin/email-invitations', 'post'>): Promise<UnwrapData<ResponseType<'/api/admin/email-invitations', 'post'>>> {
    return apiClient.post('/api/admin/email-invitations', data);
  },

  /** Revoke a pending email invitation. */
  async revoke(invitationId: number): Promise<UnwrapData<ResponseType<'/api/admin/email-invitations/{invitation_id}', 'delete'>>> {
    return apiClient.delete(`/api/admin/email-invitations/${invitationId}`);
  },
};

/**
 * Admin Activities API
 */
const adminActivities = {
  /** List activities */
  async list(params?: QueryParams<'/api/admin/activities', 'get'>): Promise<UnwrapData<ResponseType<'/api/admin/activities', 'get'>>> {
    return apiClient.get('/api/admin/activities', { params: params as Record<string, unknown> });
  },

  /** Get activity stats */
  async getStats(): Promise<UnwrapData<ResponseType<'/api/admin/activities/stats', 'get'>>> {
    return apiClient.get('/api/admin/activities/stats');
  },

  /** Health check */
  async health(): Promise<UnwrapData<ResponseType<'/api/admin/activities/health', 'get'>>> {
    return apiClient.get('/api/admin/activities/health');
  },
};

/**
 * Stats API
 */
const stats = {
  /** Get application statistics */
  async get(): Promise<{
    stats?: {
      totalCourses: number;
      departments: number;
      lastUpdated: string;
    };
    totalCourses?: number;
    departments?: number;
    lastUpdated?: string;
  }> {
    return apiClient.get('/api/stats');
  },
};

// =============================================================================
// OPTIMIZER
// =============================================================================

const optimizer = {
  /** Accept an optimizer suggestion (turns it into a pinned course) */
  async acceptSuggestion(planId: number, courseCode: string, displayOrder: number): Promise<unknown> {
    return apiClient.post(`/api/plans/${planId}/suggestions/accept`, {
      course_code: courseCode,
      display_order: displayOrder,
    });
  },

  /** Reject an optimizer suggestion (logs constraint, triggers recompute) */
  async rejectSuggestion(planId: number, courseCode: string, displayOrder: number): Promise<unknown> {
    return apiClient.post(`/api/plans/${planId}/suggestions/reject`, {
      course_code: courseCode,
      display_order: displayOrder,
    });
  },

  /** Force re-optimization of a plan */
  async optimize(planId: number, optimizerMode: OptimizerMode = 'v1'): Promise<unknown> {
    return apiClient.post(`/api/plans/${planId}/optimize`, { optimizer_mode: optimizerMode });
  },

  /** Clear all suggestions for a plan */
  async clearSuggestions(planId: number): Promise<unknown> {
    return apiClient.delete(`/api/plans/${planId}/suggestions`);
  },

  /** Lightweight status poll (avoids full plan fetch) */
  async getStatus(planId: number): Promise<{ optimization_status: Record<string, unknown> }> {
    return apiClient.get(`/api/plans/${planId}/optimizer/status`);
  },

  /** Reassign which gen-ed area an OR gen-ed course satisfies */
  async reassignGenEd(planId: number, courseCode: string, data: { gen_ed_area: string }): Promise<{
    course_code: string;
    gen_ed_area: string;
  }> {
    return apiClient.put(`/api/plans/${planId}/suggestions/${courseCode}/gen-ed-area`, data);
  },
};

// =============================================================================
// OPEN ACCESS (open-access plan §3.1)
// =============================================================================

export const access = {
  /** Public: the email domains whose school has open access (empty before
   *  launch). The sign-in page reads it before anyone is signed in. */
  async openDomains(): Promise<UnwrapData<ResponseType<'/api/access/open-domains', 'get'>>> {
    return apiClient.get('/api/access/open-domains', { skipAuth: true, retry: false });
  },
};

// =============================================================================
// MAIN EXPORT
// =============================================================================

/**
 * Type-safe API client with full IntelliSense support
 *
 * Example usage:
 * ```typescript
 * // Search courses - fully typed!
 * const result = await typedApi.courses.search({ search: 'CMSC', level: '400' });
 *
 * // Get plan - response type is inferred
 * const plan = await typedApi.academicPlans.get(123);
 *
 * // Accept the terms - request body is type-checked
 * const result = await typedApi.users.acceptTerms({ version: '2026-10-30' });
 * ```
 */
export const typedApi = {
  apExams,
  courses,
  academicPlans,
  programs,
  userPreferences,
  users,
  feedback,
  invites,
  chat,
  grades,
  otherCredits,
  transferCredits,
  requirements,
  studentProfile,
  overrides,
  stats,
  optimizer,
  access,
  admin: {
    users: adminUsers,
    chats: adminChats,
    feedback: adminFeedback,
    invites: adminInvites,
    emailInvitations: adminEmailInvitations,
    activities: adminActivities,
  },
} as const;

// Default export
export default typedApi;
